/**
 * One container as a row (user, 20/10/2026: "make the live row by row"):
 * its chip and day badge, the strip of today's drops, sent of planned with
 * the SMS, the next drop (or when it finished), and a chevron that opens
 * the card's body under it — groups, verdicts, what was held, Edit.
 * The line naming each group's list of today is gone (23/10/2026, client
 * QA: "Lets remove this" — the Day timeline's List column says it).
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC, useState } from 'react';
import { ChevronDown } from 'lucide-react';

import { cn } from '@/lib/utils';

import type { DayPlanContainerRow } from '../../../drop-generator.types';
import { instantText } from '../../../utils/clock/dk-label';
import { ContainerChip } from '../../shared/ContainerChip';
import { ContainerDayBadge, type LiveAutoSend } from '../../shared/StateBadges';
import { useCountryClock } from '../../shared/time/country-clock-context';
import { deriveContainerLive } from '../live-status';
import { DropStrip } from '../strip/DropStrip';
import { NO_CUTS } from '../../shared/cap/cap-cuts';
import { ContainerLiveBody, type ContainerLiveActions, type ContainerLiveReport } from './ContainerLiveBody';

const HEAD = 'grid grid-cols-[minmax(150px,200px)_auto_minmax(0,1fr)_auto_auto_28px] items-center gap-3';
const n = (x: number) => x.toLocaleString('en-US');

/** The next drop still to come, or null once none is left. */
const nextOf = (container: DayPlanContainerRow, now: Date): string | null =>
  container.drops
    .filter((d) => !d.runtime?.sentAt && !d.runtime?.tombstoned && new Date(d.atUtc) >= now)
    .map((d) => d.atUtc)
    .sort()[0] ?? null;

type Props = ContainerLiveActions & ContainerLiveReport & { container: DayPlanContainerRow; autoSend: LiveAutoSend; now: Date };

export const ContainerLiveRow: FC<Props> = ({ container, autoSend, now, ...body }) => {
  const cut = (body.cuts ?? NO_CUTS).cut.size;
  const [open, setOpen] = useState(false);
  const facts = deriveContainerLive(container);
  const clock = useCountryClock();
  const sms = container.drops.filter((d) => !d.runtime?.tombstoned).reduce((s, d) => s + d.amount, 0);
  const next = nextOf(container, now);
  return (
    <div className={cn('flex flex-col gap-2 rounded-lg border border-border/60 bg-card px-3 py-2', open && 'gap-3')} data-testid="live-row">
      <div className={HEAD}>
        <ContainerChip name={container.name} color={container.color} />
        <ContainerDayBadge facts={facts} autoSend={autoSend} />
        <DropStrip drops={container.drops} now={now} />
        <span className="whitespace-nowrap font-mono text-xs tabular-nums text-muted-foreground" title="Drops sent of planned · SMS planned today">
          <span className="font-semibold text-foreground">{n(facts.sent)}</span>/{n(facts.planned)} · {n(sms)} SMS
          {cut > 0 ? <span className="text-warning" title="Slots the campaign group's daily SMS cap cut today"> · {n(cut)} cut by cap</span> : null}
        </span>
        <span className="whitespace-nowrap font-mono text-[11px] tabular-nums text-muted-foreground">
          {next ? `next ${instantText(next, clock)}` : facts.state === 'not-generated' ? 'not generated' : 'nothing left'}
        </span>
        <button
          type="button"
          onClick={() => setOpen(!open)}
          aria-expanded={open}
          aria-label={`${open ? 'Hide' : 'Show'} the details of ${container.name}`}
          className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <ChevronDown className={cn('h-4 w-4 transition-transform', open && 'rotate-180')} />
        </button>
      </div>
      {open ? <ContainerLiveBody container={container} facts={facts} {...body} /> : null}
    </div>
  );
};
